// SEO-12 · /resources/board-grading-configuration-not-code-forks
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • I-03: "Board = tenant configuration, never a code branch" — board_configs +
//     report_card_templates + the 3-tier resolver core/board_config.py; NO `if board == "CBSE"`.
//   • Configurable surface: grading bands, pass mark, scale_type, NEP HPC domains/ratings, term
//     schema, attendance thresholds, AY start month; division_bands + grace_rule +
//     max_compartment_subjects are board-config DATA (defaults empty/off → byte-identical).
//   • Studio C1/C3/C5: body-section order + per-section toggles honoured in the PDF; named
//     immutable template versions with activate/rollback; the block canvas writes the order.
//   • Exam-ops D2/D7/D8: teacher_scope on marks entry, opt-in second-examiner moderation by a
//     DIFFERENT person, editing after submission invalidates moderation, schedule-conflict block.
//   • Report-card lifecycle B2: per-card approve/publish/withhold, approver != generator.
//   • Claims registry G3: AI can draft; it cannot save, approve or publish the final assessment,
//     and we never claim a complete PARAKH template pack.

const article = {
  slug: "board-grading-configuration-not-code-forks",
  title: "Grading should be configuration, not a code fork",
  seoTitle: "Board Grading as Configuration, Not Code | Vidyapeeth360",
  description:
    "CBSE, ICSE and state boards grade differently. Why a school ERP should treat the board as configuration a school can edit — and how to test that.",
  topic: "Academics",
  topicKey: "academics",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "If changing your board means waiting for the vendor to ship a release, you are not configuring a product — you are queueing behind every other school that asked first.",
  moduleLinks: ["digital-gradebook", "result-publishing", "exam-scheduling"],
  conversion: {
    to: "/contact",
    label: "Ask us to render your board's report card",
    note: "Bring last year's card. The useful demo is the one that produces yours.",
  },
  sections: [
    {
      id: "the-symptom",
      heading: "The symptom you have probably seen",
      blocks: [
        {
          t: "p",
          text: "A state-board school buys an ERP and gets a report card with a letter-grade column it does not use, a pass mark it does not apply, and a term structure that is not its own. The vendor says a custom template is possible. Six weeks later a designer sends a mock-up, and the marks still come out on the wrong scale — because the scale was never the template's decision.",
        },
        {
          t: "p",
          text: "That whole sequence is a symptom of one architectural choice: the board was written into the code rather than stored as data. Once a product contains a branch that reads \"if the board is CBSE, do this\", every additional board is a code change, every state variant is a backlog item, and no school can ever fix its own grading without the vendor.",
        },
      ],
    },
    {
      id: "the-principle",
      heading: "The principle: the board is a school's configuration",
      blocks: [
        {
          t: "p",
          text: "The alternative is to make the board a configuration record that resolves in tiers. The school's own override wins if it exists; otherwise a curated library of board definitions applies; otherwise a built-in default. A school that has never touched the settings still gets a sensible card, and a school with an unusual state variant edits its own copy without waiting for anybody.",
        },
        {
          t: "p",
          text: "This is not purism. It decides whether grading is a support ticket or a Tuesday afternoon.",
        },
      ],
    },
    {
      id: "what-is-data",
      heading: "What should be configuration, concretely",
      blocks: [
        {
          t: "p",
          text: "If a vendor tells you the board is configurable, ask which of these a school can actually change without a release.",
        },
        {
          t: "ul",
          items: [
            "**Grading bands and the pass mark.** The band table itself — label, range, and what counts as a pass — not just the colours on screen.",
            "**The scale type.** Grade points, a numeric scale, or marks and percentage. This matters more than it sounds: a marks-only board's card should drop the letter-grade column entirely rather than print an empty one.",
            "**Term structure and the academic-year start month.** An April start and a June start are both normal in India, and both change how a year's terms line up.",
            "**Attendance thresholds.** What counts as short attendance on the card, and at what percentage a warning appears.",
            "**NEP holistic domains and their rating vocabulary.** If the product supports a holistic progress card, the domains and the words used to rate them belong to the school and its board, not to the software.",
            "**Result classification.** Division bands, whether a grace rule applies and with what limits, and how many subject failures put a student into a compartment or re-examination rather than a straight fail.",
            "**Card layout.** Which sections print, and in what order. A school that does not use co-scholastic ratings should be able to remove that block rather than print an empty one.",
          ],
        },
      ],
    },
    {
      id: "one-decision",
      heading: "One decision should propagate",
      blocks: [
        {
          t: "p",
          text: "The real test of a configuration-driven design is what happens after you change the board. Selecting a board should re-derive the grading and exam configuration that the rest of the system reads — the same band table that colours a mark in the gradebook should be the one that prints on the card and the one the result classification uses. If you have to set the pass mark in three places, you do not have configuration; you have three unrelated settings that happen to be near each other.",
        },
        {
          t: "p",
          text: "The corollary is that changing a board mid-year is a governed act, not a dropdown you nudge. Expect a confirmation, and expect already-generated cards to keep the configuration they were generated under rather than silently re-rendering under new rules.",
        },
      ],
    },
    {
      id: "governance",
      heading: "Configuration without governance is worse than a code fork",
      blocks: [
        {
          t: "p",
          text: "Making grading editable raises the stakes, so the controls around who may enter, change and release marks matter as much as the flexibility itself. Four are worth insisting on.",
        },
        {
          t: "ol",
          items: [
            "**Marks entry is scoped to the teacher's own sections.** A subject teacher should not be able to open a neighbouring section's marks sheet, and this should be enforced on reading as well as on saving.",
            "**A second examiner, where used, must be a different person.** If the product offers moderation, the person who verifies must not be the person who entered — and editing a paper after submission should invalidate the earlier verification rather than leave a stale approval attached to changed marks.",
            "**Publication is deliberate and reversible in the right direction.** Releasing results to families should be an explicit action, and it should be possible to withhold one student's card inside an otherwise published term without unpublishing the whole class.",
            "**Approval separates from generation.** Whoever generates a batch of cards should not be the only signature on them.",
          ],
        },
        {
          t: "p",
          text: "One boundary is worth stating about AI on report cards. A model can draft a remark, or propose a holistic domain rating, from evidence the school has already recorded — genuinely useful when a class teacher has forty cards to write. It must not save, approve or publish the final assessment. Insist on seeing the confirmation step.",
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Board configuration is a founding architectural invariant here: the board is tenant configuration and never a code branch. A three-tier resolver reads the school's own override, then a platform-curated board library, then a built-in default — so an untouched school gets sensible grading and a school with a local variant edits its own copy.",
        },
        {
          t: "p",
          text: "The editable surface is the Report Card Studio: grading bands, pass mark, scale type, holistic domains and ratings, term schema, attendance thresholds and the academic-year start month, plus division bands, a grace rule and the compartment threshold as ordinary data. Selecting a board re-derives the report-card and exam configuration the rest of the product reads. Printed body sections can be reordered and individually switched off, and the change reaches the generated PDF rather than stopping at the preview. Designs save as named, immutable versions, so a school can roll back to last year's card without rebuilding it.",
        },
        {
          t: "p",
          text: "On the governance side, marks entry is limited to the sections a teacher is actually assigned; an optional second-examiner step requires a different person and is invalidated if the marks are edited afterwards; scheduling a paper that overlaps another for the same class-section is blocked unless an authorised person records an override; and a report card can be approved, published or withheld individually, with the approver distinct from whoever generated it. AI drafts remarks and holistic ratings from recorded evidence and stops short of saving or publishing.",
        },
        {
          t: "p",
          text: "Two boundaries worth stating plainly: we do not claim a complete PARAKH template pack, and invigilator rostering, room allocation and seating plans are not part of the exams module today. The related surfaces are [digital gradebook](/modules/digital-gradebook), [result publishing](/modules/result-publishing) and [exam scheduling](/modules/exam-scheduling).",
        },
      ],
    },
    {
      id: "the-test",
      heading: "The one-line vendor test",
      blocks: [
        {
          t: "p",
          text: "Bring last year's report card to the demo and ask for it back. Not a template that resembles it — your class, your subjects, your scale, your section order. Whether the vendor can do that in the session, by changing settings rather than promising a customisation, tells you more about the next five years than any feature list will.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
